import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

interface IUseGooglePlacesAutocompleteProps {
  placeId: string;
  onChange: (place: Partial<google.maps.places.Place>) => void;
}

export const useGooglePlacesAutocomplete = ({
  placeId,
  onChange,
}: IUseGooglePlacesAutocompleteProps) => {
  const dummyMapRef = useRef<HTMLDivElement | null>(null);
  // This is done in order to ignore the first render, so as not
  // to trigger the onChange when initializing the component
  const didInit = useRef(false);

  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<google.maps.places.AutocompleteSuggestion | null>(null);
  const [options, setOptions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);

  const handleSelect = async (newPlaceId: string, silent = false) => {
    if (newPlaceId === '') {
      if (!silent) onChange({ id: '' });
      return;
    }

    try {
      const place = new google.maps.places.Place({ id: newPlaceId });

      await place.fetchFields({
        fields: [
          'id',
          'displayName',
          'location',
          'formattedAddress',
          'addressComponents',
        ],
      });

      if (!silent) {
        onChange(place);
      }
    } catch {
      toast.error(`Error fetching place details`);
    }
  };

  useEffect(() => {
    const getPredictions = async () => {
      if (!inputValue.trim()) {
        setOptions([]);
        handleSelect('', true);
        return;
      }

      const { suggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input: inputValue,
          },
        );

      setOptions(suggestions);
    };

    getPredictions();
  }, [inputValue]);

  useEffect(() => {
    const fetchPlaceById = async () => {
      if (!placeId) return;

      try {
        const place = new google.maps.places.Place({ id: placeId });

        await place.fetchFields({
          fields: [
            'id',
            'displayName',
            'location',
            'formattedAddress',
            'addressComponents',
          ],
        });

        const { suggestions } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input: place.displayName || '',
            },
          );

        const selectedSuggestion = suggestions.find(
          entry => entry.placePrediction?.placeId === placeId,
        );

        if (selectedSuggestion) {
          setOptions([selectedSuggestion]);
          setSelectedOption(selectedSuggestion);
          setInputValue(selectedSuggestion.placePrediction?.text.text || '');

          handleSelect(placeId, !didInit.current);
        }
      } catch {
        toast.error(`Error fetching place details`);
      } finally {
        didInit.current = true;
      }
    };

    fetchPlaceById();
  }, [placeId]);

  return {
    handleSelect,
    options,
    selectedOption,
    setSelectedOption,
    inputValue,
    setInputValue,
    dummyMapRef,
  };
};
